import { ApiError } from '@trackizy/types';
import type { InternalRequest } from '../client.js';

export interface FileRecord {
  id: string;
  bucket: string;
  file_path: string;
  public_url: string;
  mime_type?: string;
  size?: number;
  [key: string]: unknown;
}

/** React Native FormData file descriptor — RN's fetch uploads
 *  {uri,name,type} objects as file parts; web passes File/Blob. */
export interface RNFileLike {
  uri: string;
  name: string;
  type: string;
}

/**
 * Storage client. Upload response exposes `public_url` + `file_path` — do
 * NOT read `.url` / `.path` (mistake produces `/uploads/undefined`).
 * `getPublicUrl(file_path)` is a last-resort fallback.
 */
export function storageEndpoints(req: InternalRequest) {
  return {
    upload: async (
      bucket: string,
      file: File | Blob | RNFileLike,
      opts?: { clientId?: string },
    ): Promise<FileRecord> => {
      const formData = new FormData();
      // Text fields MUST precede the file part: Fastify multipart's
      // request.file() only sees fields that appear before the file.
      formData.append('bucket', bucket);
      // Retry idempotency (migration 030): a repeated client_id returns the
      // original record instead of writing a duplicate file.
      if (opts?.clientId) formData.append('client_id', opts.clientId);
      // RN's FormData accepts {uri,name,type} descriptors; the cast keeps
      // this package framework-free (no react-native types).
      formData.append('file', file as Blob);
      const res = await req.raw('/storage/upload', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        // Same 401 contract as request(): a dead session clears the token so
        // the app's auth-state chokepoint sends the user back to login.
        if (res.status === 401) await Promise.resolve(req.getStorage().set(null));
        throw new ApiError(res.status, null, `Upload failed: ${res.status}`);
      }
      return (await res.json()) as FileRecord;
    },
    delete: (id: string) => req.request<void>(`/storage/${id}`, { method: 'DELETE' }),
    list: (bucket: string) => req.request<FileRecord[]>(`/storage?bucket=${bucket}`),
    getPublicUrl: (path: string) => `/uploads/${path}`,
  };
}
